// ─── ErrorState ─────────────────────────────────────────────────────────────
// Shown when a whole page or panel couldn't load. It says what happened in
// plain words and offers a way forward (usually "Try again").
//
//   <ErrorState
//     title="We couldn't load your materials"
//     message={friendlyMessage(err)}
//     onRetry={fetchMaterials}
//   />
//
// Use this only when there is nothing useful to show. When a small action
// fails (saving, removing), keep the page on screen and use a toast instead
// (see ToastProvider.jsx).

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  GREEN, GREEN_HOVER, CHARCOAL, MUTED, ERROR, ERROR_BG, ERROR_BORDER, bodyFont, labelMono,
} from '../../../theme/tokens2';

export default function ErrorState({
  title = 'Something went wrong',
  message = 'Please try again in a moment.',
  onRetry,
  retryLabel = 'Try again',
  size = 'panel', // 'panel' | 'page'
}) {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
        gap: 1.25,
        py: size === 'page' ? 12 : 6,
        px: 3,
        minHeight: size === 'page' ? '50vh' : undefined,
        justifyContent: 'center',
      }}
    >
      {/* Small "!" mark */}
      <Box aria-hidden sx={{
        width: 32, height: 32, borderRadius: '50%',
        border: `1px solid ${ERROR_BORDER}`, background: ERROR_BG, color: ERROR,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, fontSize: '15px', ...bodyFont, mb: 0.5,
      }}>
        !
      </Box>

      <Typography component="h2" sx={{ ...bodyFont, fontSize: '15px', fontWeight: 500, color: CHARCOAL }}>
        {title}
      </Typography>

      <Typography sx={{ ...bodyFont, fontSize: '13px', color: MUTED, lineHeight: 1.6, maxWidth: 420 }}>
        {message}
      </Typography>

      {onRetry && (
        <Box
          component="button"
          type="button"
          onClick={onRetry}
          sx={{
            mt: 1.5,
            ...labelMono, fontSize: '9px',
            color: GREEN, background: '#fff',
            border: `1px solid ${GREEN}`,
            px: '16px', py: '9px', minHeight: 36,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': { background: GREEN, color: '#fff' },
            '&:focus-visible': { outline: `2px solid ${GREEN_HOVER}`, outlineOffset: '2px' },
          }}
        >
          {retryLabel}
        </Box>
      )}
    </Box>
  );
}
